import { useState } from "react";
import { validationRules } from "../../../shared/utils/validationRules";
import { useCreateSite } from "../mutations";
import { ProjectFormData, SiteFormData } from "../types";

export const useAddSite = ({ onSuccess, formData }: { onSuccess: (site: any) => void, formData: ProjectFormData }) => {
  const createSite = useCreateSite();

  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>(
    {},
  );

  const [site, setSite] = useState<SiteFormData>({
    description: null,
    code: null,
    org: 1,
    region: formData.region || null,
    what3_words: null,
    address: null,
    lat: null,
    long: null,
  });
  const [submitMessage, setSubmitMessage] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validationConfig: Record<string, string[]> = {
    description: ["is_empty"],
    code: ["is_empty"],
    address: ["is_empty"],
    org: ["is_empty"],
    region: ["is_empty"],
  };

  const validateField = (key: string, value: any): string | null => {
    const rules = validationConfig[key];
    if (!rules) return null;
    for (const ruleName of rules) {
      const rule = validationRules[ruleName];
      if (rule) {
        const error = rule(value);
        if (error) return error;
      }
    }
    return null;
  };

  const handleFieldChange = (key: string, value: any) => {
    setSite((prev) => ({ ...prev, [key]: value }));
  };

  const handleAddressChange = (addressData: {
    address: string;
    what3_words: string;
    lat: number;
    long: number;
  }) => {
    setSite((prev) => ({
      ...prev,
      address: addressData.address,
      what3_words: addressData.what3_words,
      lat: addressData.lat,
      long: addressData.long,
    }));
    const error = validateField("address", addressData.address);
    setFieldErrors((prev) => ({ ...prev, address: error }));
  };

  const validateAllFields = (): { isAllFieldValid: boolean; errors: any } => {
    let newErrors: Record<string, string | null> = {};
    Object.keys(validationConfig).forEach((key) => {
      const error = validateField(key, (site as any)[key]);
      if (error) newErrors[key] = error;
    });
    setFieldErrors(newErrors);
    const isAllFieldValid = Object.values(newErrors).every((error) => !error);
    return { isAllFieldValid, errors: newErrors };
  };

  const handleSubmit = async () => {
    setSubmitMessage(null);
    setIsSubmitting(true);

    const { isAllFieldValid, errors: errorInFields } = validateAllFields();

    if (!isAllFieldValid) {
      setIsSubmitting(false);
      return;
    }

    const payload: any = {
      description: site.description,
      code: site.code,
      org_id: site.org,
      network_id: site.region,
      what3_words: site.what3_words,
      address: site.address,
      latitude: site.lat,
      longitude: site.long,
      is_third_party: true,
    };

    try {
      const newSite = await createSite.mutateAsync(payload);
      setSubmitMessage({
        type: "success",
        message: "Site created successfully!",
      });
      setSite({
        description: null,
        code: null,
        org: 1,
        region: null,
        what3_words: null,
        address: null,
        lat: null,
        long: null,
      });
      onSuccess(newSite?.data);
    } catch (error: any) {
      setSubmitMessage({
        type: "error",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to create site. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    site,
    fieldErrors,
    handleFieldChange,
    handleAddressChange,
    handleSubmit,
    isSubmitting,
    submitMessage
  };
};
